import { type TFunction } from 'i18next';

export const AUTH_METHODS = ['apiKey', 'oauth'] as const;
export const DISTRIBUTIONS = ['npm', 'docker'] as const;
export const CLIENT_IDS = ['vscode', 'cursor', 'other'] as const;

export type AuthMethod = (typeof AUTH_METHODS)[number];
export type Distribution = (typeof DISTRIBUTIONS)[number];
export type ClientId = (typeof CLIENT_IDS)[number];

export interface Selections {
  auth: AuthMethod;
  distribution: Distribution;
  client: ClientId;
}

interface LaunchSpec {
  command: string;
  args: string[];
  env?: Record<string, string>;
}

type ApiKeyMode = 'prompt' | 'env' | 'inline';

type ClientSpec = {
  rootKey: 'servers' | 'mcpServers';
  apiKeyMode: Record<Distribution, ApiKeyMode>;
} & (
  | { kind: 'file'; shortName: string; configPath: string }
  | { kind: 'generic' }
);

export interface CodeBlock {
  label: string;
  code: string;
  language: 'json' | 'shell' | 'text';
}

export interface GuideSection {
  heading: string;
  body: string;
  link?: { text: string; href: string };
  blocks: CodeBlock[];
  note?: string;
}

export interface Guide {
  title: string;
  intro: string;
  summary?: { label: string; value: string }[];
  sections: GuideSection[];
  footer?: string;
}

const LAUNCH: Record<AuthMethod, Record<Distribution, LaunchSpec>> = {
  apiKey: {
    npm: { command: 'npx', args: ['-y', 'axe-mcp-server'] },
    docker: {
      command: 'docker',
      args: [
        'run',
        '--add-host=host.docker.internal:host-gateway',
        '-i',
        '--rm',
        '-e',
        'AXE_SERVER_URL',
        '-e',
        'AXE_API_KEY',
        'dequesystems/axe-mcp-server:latest'
      ]
    }
  },
  oauth: {
    npm: {
      command: 'npx',
      args: [
        '-y',
        '@deque/axe-auth',
        'run',
        '--',
        'npx',
        '-y',
        'axe-mcp-server'
      ],
      env: { AXE_TOKEN_REFRESH_PORT: '9223' }
    },
    docker: {
      command: 'npx',
      args: [
        '-y',
        '@deque/axe-auth',
        'run',
        '--',
        'docker',
        'run',
        '--add-host=host.docker.internal:host-gateway',
        '-i',
        '--rm',
        '-p',
        '127.0.0.1:9223:9223',
        '-e',
        'AXE_ACCESS_TOKEN',
        '-e',
        'AXE_TOKEN_REFRESH_PORT',
        '-e',
        'AXE_TOKEN_REFRESH_SECRET',
        '-e',
        'AXE_TOKEN_REFRESH_HOST=0.0.0.0',
        'dequesystems/axe-mcp-server:latest'
      ],
      env: { AXE_TOKEN_REFRESH_PORT: '9223' }
    }
  }
};

const CLIENTS: Record<ClientId, ClientSpec> = {
  vscode: {
    kind: 'file',
    rootKey: 'servers',
    shortName: 'VS Code',
    configPath: '.vscode/mcp.json',
    apiKeyMode: { npm: 'inline', docker: 'prompt' }
  },
  cursor: {
    kind: 'file',
    rootKey: 'mcpServers',
    shortName: 'Cursor',
    configPath: '.cursor/mcp.json',
    apiKeyMode: { npm: 'inline', docker: 'env' }
  },
  other: {
    kind: 'generic',
    rootKey: 'mcpServers',
    apiKeyMode: { npm: 'inline', docker: 'inline' }
  }
};

const apiKeyModeFor = ({
  auth,
  distribution,
  client
}: Selections): ApiKeyMode | undefined =>
  auth === 'apiKey' ? CLIENTS[client].apiKeyMode[distribution] : undefined;

export const getClientLabels = (t: TFunction): Record<ClientId, string> => ({
  vscode: t('VS Code with Copilot'),
  cursor: 'Cursor',
  other: t('Other')
});

const API_KEY_VALUE: Record<ApiKeyMode, string> = {
  prompt: '${input:axe-api-key}',
  env: '${env:AXE_API_KEY}',
  inline: 'your-api-key-here'
};

const AXE_AUTH_LOGIN = 'npx -y @deque/axe-auth login';

const PLUGIN_DOCS_URL =
  'https://docs.deque.com/devtools-server/en/claude-code-plugin';

const AXE_API_KEY_INPUT = {
  type: 'promptString',
  id: 'axe-api-key',
  description: 'axe MCP Server API Key',
  password: true
};

export const buildConfig = (selections: Selections): string => {
  const { auth, distribution, client: clientId } = selections;
  const client = CLIENTS[clientId];
  const launch = LAUNCH[auth][distribution];

  const mode = apiKeyModeFor(selections);
  const finalLaunch = mode
    ? { ...launch, env: { ...launch.env, AXE_API_KEY: API_KEY_VALUE[mode] } }
    : launch;

  const config: Record<string, unknown> = {};
  if (mode === 'prompt') {
    config.inputs = [AXE_API_KEY_INPUT];
  }
  config[client.rootKey] = { 'axe-mcp-server': finalLaunch };

  return JSON.stringify(config, null, 2);
};

const buildInstallSection = (
  t: TFunction,
  distribution: Distribution
): GuideSection => {
  if (distribution === 'docker') {
    return {
      heading: t('Install what you need'),
      body: t(
        'Pull the image. It already includes Chromium, so there is nothing else to install.'
      ),
      blocks: [
        {
          label: t('Pull command'),
          code: 'docker pull dequesystems/axe-mcp-server:latest',
          language: 'shell'
        }
      ],
      note: t(
        'Docker does not update an image you already pulled. Pull again when you want a newer version.'
      )
    };
  }

  return {
    heading: t('Install what you need'),
    body: t(
      'Install the Chromium build the server pins today. The server updates itself on every start, so when it moves to a newer build a scan will fail with the exact command to run. Run that command.'
    ),
    blocks: [
      {
        label: t('Install command'),
        code: 'npx -y playwright@$(npm view axe-mcp-server dependencies.playwright) install chromium',
        language: 'shell'
      }
    ],
    note: t(
      'The server updates itself on every start, so if a scan later fails saying Chromium is not installed, run the command from the error message. It names the version the running server actually needs. On Windows cmd.exe, run {{command}} first and paste the version into the command.',
      { command: 'npm view axe-mcp-server dependencies.playwright' }
    )
  };
};

const buildCredentialSection = (
  t: TFunction,
  selections: Selections,
  apiKeysUrl: string
): GuideSection => {
  if (selections.auth === 'oauth') {
    return {
      heading: t('Sign in with your browser'),
      body: t(
        'Sign in once with the @deque/axe-auth helper. It opens your browser and stores a short-lived token in your operating system keychain.'
      ),
      blocks: [
        {
          label: t('Sign-in command'),
          code: AXE_AUTH_LOGIN,
          language: 'shell'
        }
      ],
      note: t(
        'The configuration below starts the server through @deque/axe-auth, which keeps the token fresh for as long as the session lasts.'
      )
    };
  }

  const mode = apiKeyModeFor(selections);
  const blocks: CodeBlock[] = [];
  if (mode === 'env') {
    blocks.push({
      label: t('Environment variable'),
      code: 'export AXE_API_KEY="your-api-key-here"',
      language: 'shell'
    });
  }

  let note: string | undefined;
  if (mode === 'env') {
    note = t(
      'Add this line to your shell profile, then restart Cursor so it can read the key. On Windows, run {{command}} instead, then restart Cursor.',
      { command: 'setx AXE_API_KEY "your-api-key-here"' }
    );
  } else if (mode === 'prompt') {
    note = t('VS Code asks for the key the first time the server starts.');
  } else if (mode === 'inline') {
    note = t(
      'Replace {{placeholder}} in the configuration below with your key.',
      { placeholder: API_KEY_VALUE.inline }
    );
  }

  return {
    heading: t('Get your API key'),
    body: t(
      'Create a key on the API Keys page, choose Axe MCP Server as the product, and copy it. This is the only time it is shown.'
    ),
    link: { text: t('API Keys page'), href: apiKeysUrl },
    blocks,
    note
  };
};

const buildAddServerSection = (
  t: TFunction,
  selections: Selections
): GuideSection => {
  const client = CLIENTS[selections.client];
  const code = buildConfig(selections);

  if (client.kind === 'generic') {
    return {
      heading: t('Add the server to your MCP client'),
      body: t("Add this entry to your client's MCP configuration file."),
      blocks: [{ label: t('MCP configuration'), code, language: 'json' }]
    };
  }

  return {
    heading: t('Add the server to {{client}}', { client: client.shortName }),
    body: t(
      'Create this file in your project root, or add the axe-mcp-server entry to your existing one.'
    ),
    blocks: [{ label: client.configPath, code, language: 'json' }]
  };
};

const buildVerifySection = (
  t: TFunction,
  selections: Selections
): GuideSection => {
  const clientId = selections.client;
  const mode = apiKeyModeFor(selections);

  let body: string;
  if (clientId === 'vscode') {
    body =
      mode === 'prompt'
        ? t(
            'Open the file, click Start above the axe-mcp-server entry, and enter your key when prompted. VS Code should then list axe-mcp-server with the analyze and remediate tools. analyze finds issues, remediate suggests fixes. Then open Copilot chat and try this.'
          )
        : t(
            'Open the file, click Start above the axe-mcp-server entry. VS Code should then list axe-mcp-server with the analyze and remediate tools. analyze finds issues, remediate suggests fixes. Then open Copilot chat and try this.'
          );
  } else if (clientId === 'cursor') {
    body = t(
      'Open Customize in the Cursor sidebar and check that axe-mcp-server is listed and turned on. Then open Cursor chat and try this.'
    );
  } else {
    body = t(
      'Restart your client and confirm axe-mcp-server is listed with the analyze and remediate tools. Then try this prompt.'
    );
  }

  return {
    heading: t('Verify and run your first scan'),
    body,
    blocks: [
      {
        label: t('Example prompt'),
        code: 'Analyze https://example.com for accessibility issues.',
        language: 'text'
      }
    ]
  };
};

export const buildManualGuide = (
  selections: Selections,
  t: TFunction,
  apiKeysUrl: string
): Guide => {
  return {
    title: t('Your setup guide is ready'),
    intro: t('Four steps to a working connection.'),
    summary: [
      {
        label: t('Authentication'),
        value: selections.auth === 'apiKey' ? t('API key') : 'OAuth'
      },
      {
        label: t('Distribution'),
        value: selections.distribution === 'npm' ? 'npm' : 'Docker'
      },
      { label: t('MCP client'), value: getClientLabels(t)[selections.client] }
    ],
    sections: [
      buildInstallSection(t, selections.distribution),
      buildCredentialSection(t, selections, apiKeysUrl),
      buildAddServerSection(t, selections),
      buildVerifySection(t, selections)
    ],
    footer: t(
      'To switch distribution later, generate a new guide and replace the whole axe-mcp-server entry in your config.'
    )
  };
};

export const buildPluginGuide = (t: TFunction, apiKeysUrl: string): Guide => ({
  title: t('Set up with the Axe plugin'),
  intro: t(
    'Five steps to a working connection. You need Claude Code with plugin support and an axe account with access to axe MCP Server.'
  ),
  sections: [
    {
      heading: t('Get your credential'),
      body: t(
        'The server needs one credential. For an API key, create one on the API Keys page, choose axe MCP Server as the product, and copy it. This is the only time the key is shown.'
      ),
      link: { text: t('API Keys page'), href: apiKeysUrl },
      blocks: [
        {
          label: t('Sign-in command'),
          code: AXE_AUTH_LOGIN,
          language: 'shell'
        }
      ],
      note: t(
        'To sign in with your browser instead, run the sign-in command. If you set both, the plugin uses the browser sign-in.'
      )
    },
    {
      heading: t('Install the Axe plugin'),
      body: t(
        'Add the Deque marketplace, then install the plugin. When Claude Code asks for the Axe API key, paste your key, or leave it empty if you signed in with your browser. To change it later, run {{command}} and manage axe-accessibility.',
        { command: '/plugin' }
      ),
      link: {
        text: t('Claude Code plugin documentation'),
        href: PLUGIN_DOCS_URL
      },
      blocks: [
        {
          label: t('Claude Code: add the marketplace'),
          code: '/plugin marketplace add dequelabs/axe-accessibility',
          language: 'text'
        },
        {
          label: t('Claude Code: install the plugin'),
          code: '/plugin install axe-accessibility',
          language: 'text'
        }
      ],
      note: t(
        'Restart Claude Code before the next step. The MCP server entry is only registered on restart.'
      )
    },
    {
      heading: t('Run the guided setup'),
      body: t(
        'One command asks which runtime to use, checks your credential, and configures one client. Run it again for each of Cursor, VS Code with Copilot or Claude Desktop.'
      ),
      blocks: [
        {
          label: t('Claude Code: run the guided setup'),
          code: '/axe-accessibility:mcp-setup',
          language: 'text'
        }
      ],
      note: t(
        'npm needs Node.js 22.19.0 or later, plus a one-time Chromium install. Docker needs the daemon running. The guided setup asks which one you have.'
      )
    },
    {
      heading: t('Confirm the server is connected'),
      body: t(
        'Run this in Claude Code. The axe-mcp-server entry should be listed as connected, with the analyze and remediate tools available.'
      ),
      blocks: [
        {
          label: t('Claude Code: list MCP servers'),
          code: '/mcp',
          language: 'text'
        }
      ],
      note: t(
        'If it is not listed, restart Claude Code and run the command again.'
      )
    },
    {
      heading: t('Run your first scan'),
      body: t(
        'Ask for a scan in plain language. The server returns the accessibility violations it finds, along with the code that causes them.'
      ),
      blocks: [
        {
          label: t('Try this prompt'),
          code: 'Analyze https://dequeuniversity.com/demo/mars for accessibility issues',
          language: 'text'
        }
      ],
      note: t(
        'A first scan returns a sizable payload. Tens of kilobytes is normal.'
      )
    }
  ],
  footer: t(
    'The plugin adds two more commands. {{instructionsCommand}} writes the accessibility workflow into {{claudeFile}}, {{copilotFile}}, Cursor rules or {{agentsFile}}. {{auditCommand}} runs a remediation loop on a URL until violations reach zero. The guide you download keeps both.',
    {
      instructionsCommand: '/axe-accessibility:mcp-generate-instructions',
      claudeFile: 'CLAUDE.md',
      copilotFile: '.github/copilot-instructions.md',
      agentsFile: 'AGENTS.md',
      auditCommand: '/axe-accessibility:mcp-audit'
    }
  )
});
